import { BoardNote } from "@/bryntum-lib/models/BoardNote";
import { useSwivuRichEditor } from "@/react/hooks/useSwivuRichEditor";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useEntityNotesContext } from "@/react/components/entityViews/notes/context/EntityNotesContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useCallback, useEffect, useRef, useState } from "react";

export const useEntityNotesView = () => {
  const richEditorRef = useRef<HTMLDivElement>(null);
  const [activeNote, setActiveNote] = useState<BoardNote | null>(null);
  const [readOnly, setReadonly] = useState<boolean>(true);
  const [originalNoteData, setOriginalNoteData] = useState<string>("");
  const { notes, getNoteData, loading, saveNote, refreshNotes, entityRecord } = useEntityNotesContext();
  const { initialize, isInitialized, setNoteData, hasChanges, noteData, markAsSaved, setEditorReadOnly, isEmpty, handleUninitialize } = useSwivuRichEditor({ height: 400 });
  const { version } = useSwivuStore<BoardNote>({ storeId: STORE_KEYS.boardNotesStore, subscriberFn: ({ record: changedRecord }) => changedRecord[entityRecord.entityType] === entityRecord.id });

  const fetchAndDisplayCurrentNote = useCallback(async (note: BoardNote) => {
    const noteData = await getNoteData(note.id);
    const data = noteData || "";
    setNoteData(data);
    setOriginalNoteData(data);
  }, [getNoteData, setNoteData]);

  const setActiveNoteData = useCallback(async (note: BoardNote) => {
    if (activeNote?.id === note.id) return;
    await fetchAndDisplayCurrentNote(note);
    setActiveNote(note);
    setReadonly(true);
  }, [activeNote, fetchAndDisplayCurrentNote]);

  const handleSave = useCallback(async () => {
    if (activeNote) {
      await saveNote(activeNote, { content: noteData, board: activeNote.boardId, archived: activeNote.archived });
      markAsSaved();
      setOriginalNoteData(noteData);
      setReadonly(true);
    }
  }, [activeNote, noteData, saveNote, markAsSaved]);

  const handleCancel = useCallback(() => {
    setNoteData(originalNoteData);
    setReadonly(true);
  }, [originalNoteData, setNoteData]);

  const handleDeleteNote = useCallback(() => {
    const remainingNotes = notes.filter((note) => note.id !== activeNote?.id);
    if (remainingNotes.length > 0) {
      const nextNote = remainingNotes[0];
      setActiveNote(nextNote);
      fetchAndDisplayCurrentNote(nextNote);
    } else {
      setActiveNote(null);
      setNoteData("");
    }
  }, [notes, activeNote, fetchAndDisplayCurrentNote, setNoteData]);

  useEffect(() => {
    if (richEditorRef.current && notes.length && !isInitialized) {
      initialize(richEditorRef.current);
    } else if (!notes.length && isInitialized) {
      handleUninitialize();
    }
  }, [richEditorRef, notes.length, isInitialized]);

  useEffect(() => {
    const activateFirstNote = async () => {
      const [noteToActivate] = notes;
      // Only activate if the note belongs to the current entity record
      if (noteToActivate && noteToActivate[entityRecord.entityType] === entityRecord.id) {
        const noteData = await getNoteData(noteToActivate.id);
        const data = noteData || "";
        setNoteData(data);
        setOriginalNoteData(data);
        setActiveNote(noteToActivate);
        setReadonly(true);
      }
    }

    if (isInitialized && !activeNote && notes.length) {
      activateFirstNote();
    }
  }, [isInitialized, notes.length, activeNote, getNoteData, setNoteData, entityRecord])

  useEffect(() => {
    if (isInitialized) {
      setEditorReadOnly(readOnly);
    }
  }, [readOnly, isInitialized, setEditorReadOnly]);

  useEffect(() => {
    refreshNotes();
    if (activeNote) {
      fetchAndDisplayCurrentNote(activeNote);
    }
  }, [refreshNotes, version]);

  // Reset active note when entity record changes
  useEffect(() => {
    setActiveNote(null);
    setNoteData("");
    setOriginalNoteData("");
  }, [entityRecord.id, setNoteData]);

  return {
    richEditorRef,
    activeNote,
    readOnly,
    setReadonly,
    notes,
    loading,
    hasChanges,
    isEmpty,
    entityRecord,
    handleSave,
    handleCancel,
    handleDeleteNote,
    setActiveNoteData,
    hasNotes: notes.length !== 0
  };
};
